/**
 * Session keeper — продление логин-сессии живой вкладкой (эпик SESSRENEW, O5/O7/O8/O9).
 *
 * Чистое ядро без Vue/Pinia/DOM: всё окружение (часы, хранилище, сеть, Web Lock,
 * таймеры, события окна) приходит через `SessionKeeperDeps` — гоняется в vitest
 * на fake timers. Pinia-обёртка с дефолтными deps — `session-store.ts`.
 *
 * Правила:
 *  - время продления = локальное время получения текущего токена + `renewAfterSec`
 *    (нет отметки — `iat` токена). Часы клиента НЕ решают терминальность: токен «за exp»
 *    по часам клиента даёт немедленную попытку, а «истекла» — только 401 сервера (R5);
 *  - токен без `jti` не продлевается (O3/R6): доживает свой срок, фаза — по `exp`;
 *  - у попытки дедлайн (`renewTimeoutMs`, 15 с): зависший fetch абортится → временный отказ;
 *  - продление под `withLock('omniflow-session-renew:' + jti)`, внутри токен перечитывается:
 *    сменился (продлила другая вкладка) — ничего не делаем; не сменился — плановая попытка заново
 *    сверяет срок по свежей мете (E1: соседка могла узнать о новом токене раньше его отметки),
 *    явная (`renewNow`: кнопка, boot-проверка R15) продлевает всегда;
 *  - временный отказ → `failing`, повтор через 30 с · 2ⁿ (потолок 10 мин); тик расписания
 *    паузу не обгоняет, `online`/`visibilitychange→visible` — немедленная попытка со сбросом паузы.
 */
import { RenewError, parseJwtPayload } from './auth'
import type { RenewResult, TokenMeta } from './auth'

export type SessionPhase = 'ok' | 'renewing' | 'renewed' | 'failing' | 'expiring' | 'expired'

export interface SessionState {
  phase: SessionPhase
  /** `exp` текущего токена, ms (часы клиента). */
  expiresAt: number | null
  /** Момент последнего успешного продления этой вкладкой, ms. */
  lastRenewAt: number | null
  lastError: string | null
  /** Когда будет следующая попытка (расписание или пауза после отказа), ms. */
  nextRenewAt: number | null
}

export type SessionWakeEvent = 'visibilitychange' | 'online'

export interface SessionKeeperDeps {
  now(): number
  readToken(): string | null
  readTokenMeta(): TokenMeta
  /** Записать новый токен вместе с порогом продления (отметку получения ставит хранилище). */
  writeToken(token: string, renewAfterSec: number): void
  /** `signal` абортится по дедлайну попытки — fetch не висит на полуоткрытом TCP. */
  renew(token: string, signal?: AbortSignal): Promise<RenewResult>
  withLock(name: string, fn: () => Promise<void>): Promise<void>
  /**
   * Терминальный отказ (401) — модалка перелогина. Зовётся один раз на токен; `err.closed` —
   * объяснение сервера, почему сессия закрыта (O13), для текста модалки.
   */
  onTerminal(err: RenewError): void
  setInterval(fn: () => void, ms: number): unknown
  clearInterval(handle: unknown): void
  setTimeout(fn: () => void, ms: number): unknown
  clearTimeout(handle: unknown): void
  /**
   * Подписка на пробуждение. Для `visibilitychange` реализация обязана звать `fn`
   * только при переходе во `visible`. Возвращает отписку.
   */
  addWindowListener(type: SessionWakeEvent, fn: () => void): () => void
}

export interface SessionKeeperOptions {
  /** Период тика расписания, ms (UI: 30 000). */
  tickMs: number
  /** Фолбэк порога продления, если сервер его не прислал (UI: 3600). */
  renewAfterSec: number
  /** Дедлайн одной попытки renew, ms (дефолт 15 000): по нему — временный отказ, блокировка отпускается. */
  renewTimeoutMs?: number
}

export interface SessionKeeper {
  start(): void
  stop(): void
  /** Попытка продления немедленно (кнопка «Продлить сейчас»), под той же блокировкой. */
  renewNow(): Promise<void>
  /**
   * Сверить состояние с токеном хранилища сейчас, не дожидаясь тика: токен сменился в этой
   * вкладке (перелогин в модалке, принят от соседней) — значок не должен ещё до 30 с показывать
   * исход прежнего токена («Сессия истекла»). До `start()` — только сверка, без продления.
   */
  sync(): void
  readonly state: Readonly<SessionState>
  onChange(cb: (state: Readonly<SessionState>) => void): () => void
}

export const SESSION_LOCK_PREFIX = 'omniflow-session-renew:'
const RENEWED_SHOW_MS = 4_000
const EXPIRING_WINDOW_MS = 3_600_000
const BACKOFF_BASE_MS = 30_000
export const DEFAULT_RENEW_TIMEOUT_MS = 15_000
const BACKOFF_MAX_MS = 600_000

const readClaims = (token: string): { jti: string | null; iatMs: number | null; expMs: number | null } => {
  const claims = parseJwtPayload(token)
  return {
    jti: claims && typeof claims.jti === 'string' ? claims.jti : null,
    iatMs: claims && typeof claims.iat === 'number' ? claims.iat * 1000 : null,
    expMs: claims && typeof claims.exp === 'number' ? claims.exp * 1000 : null,
  }
}

const errorMessage = (err: unknown): string => (err instanceof Error ? err.message : String(err))

const sameState = (left: SessionState, right: SessionState): boolean =>
  left.phase === right.phase &&
  left.expiresAt === right.expiresAt &&
  left.lastRenewAt === right.lastRenewAt &&
  left.lastError === right.lastError &&
  left.nextRenewAt === right.nextRenewAt

export const createSessionKeeper = (deps: SessionKeeperDeps, opts: SessionKeeperOptions): SessionKeeper => {
  let snapshot: SessionState = { phase: 'ok', expiresAt: null, lastRenewAt: null, lastError: null, nextRenewAt: null }
  const listeners = new Set<(state: Readonly<SessionState>) => void>()

  let started = false
  let tickHandle: unknown = null
  let retryHandle: unknown = null
  let renewedHandle: unknown = null
  let unsubscribers: Array<() => void> = []

  /** Токен, к которому относятся исход/пауза/терминальность. Сменился снаружи — всё сбрасывается. */
  let trackedToken: string | null = null
  let lastOutcome: 'success' | 'failure' | null = null
  let lastError: string | null = null
  let lastRenewAt: number | null = null
  let failCount = 0
  let retryAt: number | null = null
  let terminal = false
  let renewing = false
  let showRenewed = false
  let inFlight: Promise<void> | null = null
  /** Попытку (текущую или ближайшую) заказал `renewNow` — срок под блокировкой не перепроверяется. */
  let explicitRequested = false
  const renewTimeoutMs = opts.renewTimeoutMs ?? DEFAULT_RENEW_TIMEOUT_MS

  const clearRetry = () => {
    if (retryHandle !== null) deps.clearTimeout(retryHandle)
    retryHandle = null
    retryAt = null
  }

  const resetBackoff = () => {
    clearRetry()
    failCount = 0
  }

  /** Токен в хранилище сменился не нами (другая вкладка, перелогин, выход) — прежний исход не про него. */
  const syncToken = (token: string | null) => {
    if (token === trackedToken) return
    trackedToken = token
    resetBackoff()
    lastOutcome = null
    lastError = null
    terminal = false
  }

  /** Момент, когда текущий токен пора продлевать по расписанию. */
  const dueAtOf = (token: string): number => {
    const meta = deps.readTokenMeta()
    const { iatMs, expMs } = readClaims(token)
    const now = deps.now()
    // R5: «за exp» по часам клиента — не «истекла», а повод спросить сервер прямо сейчас.
    if (expMs !== null && expMs <= now) return now
    const base = meta.receivedAt ?? iatMs
    if (base === null) return now
    return base + (meta.renewAfterSec ?? opts.renewAfterSec) * 1000
  }

  const derivePhase = (expMs: number | null, now: number): SessionPhase => {
    if (terminal) return 'expired'
    if (renewing) return 'renewing'
    if (showRenewed) return 'renewed'
    if (expMs !== null && expMs - now < EXPIRING_WINDOW_MS && lastOutcome !== 'success') return 'expiring'
    if (lastOutcome === 'failure') return 'failing'
    return 'ok'
  }

  const publish = () => {
    const token = deps.readToken()
    const now = deps.now()
    const expMs = token ? readClaims(token).expMs : null
    const next: SessionState = {
      phase: derivePhase(expMs, now),
      expiresAt: expMs,
      lastRenewAt,
      lastError,
      nextRenewAt: !token || terminal || renewableJti(token) === null ? null : (retryAt ?? dueAtOf(token)),
    }
    if (sameState(snapshot, next)) return
    snapshot = next
    for (const cb of listeners) cb(snapshot)
  }

  const onSuccess = (result: RenewResult) => {
    lastOutcome = 'success'
    lastError = null
    lastRenewAt = deps.now()
    resetBackoff()
    trackedToken = result.token
    // После stop() таймер «Продлено» не взводим — иначе он пережил бы остановку keeper'а.
    if (!started) return
    showRenewed = true
    if (renewedHandle !== null) deps.clearTimeout(renewedHandle)
    renewedHandle = deps.setTimeout(() => {
      renewedHandle = null
      showRenewed = false
      publish()
    }, RENEWED_SHOW_MS)
  }

  const onFailure = (err: unknown) => {
    lastError = errorMessage(err)
    if (err instanceof RenewError && err.status === 401) {
      console.error('session keeper: сессия не продлевается (401), нужен перелогин', err)
      clearRetry()
      const firstTime = !terminal
      terminal = true
      // После stop() (выход во время попытки) модалку не поднимаем: сессию завершили сами.
      if (firstTime && started) deps.onTerminal(err)
      return
    }
    console.error('session keeper: продление не удалось, повтор с паузой', err)
    lastOutcome = 'failure'
    failCount += 1
    const delay = Math.min(BACKOFF_BASE_MS * 2 ** (failCount - 1), BACKOFF_MAX_MS)
    clearRetry()
    retryAt = deps.now() + delay
    if (started) {
      retryHandle = deps.setTimeout(() => {
        retryHandle = null
        check()
      }, delay)
    }
  }

  /**
   * Один вызов renew с дедлайном: зависший fetch (полуоткрытый TCP после сна — у fetch таймаута нет)
   * абортится и превращается во временный отказ, блокировка и inFlight отпускаются.
   */
  const renewWithDeadline = async (token: string): Promise<RenewResult> => {
    const controller = typeof AbortController === 'undefined' ? null : new AbortController()
    let deadlineHandle: unknown = null
    const deadline = new Promise<never>((_resolve, reject) => {
      deadlineHandle = deps.setTimeout(() => {
        deadlineHandle = null
        controller?.abort()
        reject(new RenewError(`renew failed: нет ответа за ${renewTimeoutMs} мс`))
      }, renewTimeoutMs)
    })
    try {
      const request = controller ? deps.renew(token, controller.signal) : deps.renew(token)
      return await Promise.race([request, deadline])
    } finally {
      if (deadlineHandle !== null) deps.clearTimeout(deadlineHandle)
    }
  }

  const attempt = (decisionToken: string, jti: string): Promise<void> => {
    if (inFlight) return inFlight
    const lockName = SESSION_LOCK_PREFIX + jti
    // Маркер попытки ставится ДО любого publish(): синхронный onChange-слушатель,
    // зовущий renewNow(), получит эту же попытку, а не начнёт вторую.
    let release: () => void = () => {}
    const run = new Promise<void>((resolve) => {
      release = resolve
    })
    inFlight = run
    void (async () => {
      try {
        await deps.withLock(lockName, async () => {
          const current = deps.readToken()
          const explicit = explicitRequested
          explicitRequested = false
          // Пока ждали блокировку, продлила другая вкладка (или выход/перелогин) — не наше дело.
          if (current !== decisionToken) return
          // E1: решение «пора» могло опираться на устаревшую отметку получения — перепроверить по свежей.
          if (!explicit && deps.now() < dueAtOf(current)) return
          renewing = true
          publish()
          try {
            const result = await renewWithDeadline(current)
            deps.writeToken(result.token, result.renewAfterSec)
            onSuccess(result)
          } catch (err) {
            onFailure(err)
          } finally {
            renewing = false
          }
        })
      } catch (err) {
        // Отказ самого Web Lock API — временный, как сеть.
        renewing = false
        onFailure(err)
      } finally {
        explicitRequested = false
        inFlight = null
        syncToken(deps.readToken())
        publish()
        release()
      }
    })()
    return run
  }

  /** jti текущего токена; без jti токен не продлевается (O3/R6) — доживает свой срок. */
  const renewableJti = (token: string): string | null => readClaims(token).jti

  /** Сверить срок и, если пора и пауза не держит, продлить. */
  const check = () => {
    const token = deps.readToken()
    syncToken(token)
    const jti = token ? renewableJti(token) : null
    if (token && jti !== null && !terminal && !inFlight) {
      const now = deps.now()
      if (now >= dueAtOf(token) && (retryAt === null || now >= retryAt)) {
        void attempt(token, jti)
        return
      }
    }
    publish()
  }

  /** Пробуждение (сеть вернулась, вкладка стала видимой): пауза сбрасывается, сверка сразу. */
  const wake = () => {
    resetBackoff()
    check()
  }

  return {
    start: () => {
      if (started) return
      started = true
      tickHandle = deps.setInterval(check, opts.tickMs)
      unsubscribers = [
        deps.addWindowListener('visibilitychange', wake),
        deps.addWindowListener('online', wake),
      ]
      check()
    },
    stop: () => {
      if (!started) return
      started = false
      if (tickHandle !== null) deps.clearInterval(tickHandle)
      tickHandle = null
      clearRetry()
      if (renewedHandle !== null) deps.clearTimeout(renewedHandle)
      renewedHandle = null
      showRenewed = false
      for (const unsubscribe of unsubscribers) unsubscribe()
      unsubscribers = []
    },
    sync: () => {
      if (started) {
        check()
        return
      }
      syncToken(deps.readToken())
      publish()
    },
    renewNow: async () => {
      const token = deps.readToken()
      syncToken(token)
      const jti = token ? renewableJti(token) : null
      if (!token || jti === null || terminal) {
        publish()
        return
      }
      resetBackoff()
      explicitRequested = true
      await attempt(token, jti)
    },
    get state() {
      return snapshot
    },
    onChange: (cb) => {
      listeners.add(cb)
      return () => {
        listeners.delete(cb)
      }
    },
  }
}

/** Минимум `navigator.locks`, который нужен keeper'у. */
export interface LockRequester {
  request(name: string, callback: () => Promise<void>): Promise<unknown>
}

/**
 * Дефолтный `withLock`: Web Lock, если он есть. Без `navigator.locks` (не secure context)
 * исполняет `fn` без блокировки и один раз предупреждает — повторная проверка токена
 * внутри keeper'а всё равно гасит большинство дублей.
 */
export const createDefaultWithLock = (locks: LockRequester | undefined) => {
  let warned = false
  return async (name: string, fn: () => Promise<void>): Promise<void> => {
    if (!locks) {
      if (!warned) {
        warned = true
        console.warn('session keeper: navigator.locks недоступен — продление без межвкладочной блокировки')
      }
      await fn()
      return
    }
    await locks.request(name, fn)
  }
}
